'use client';

import * as React from 'react';
import type { DateSelectArg, EventClickArg, EventDropArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import Calendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CalendarBlank as CalendarBlankIcon } from '@phosphor-icons/react/dist/ssr';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/paths';
import { useDialog } from '@/hooks/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { effectiveCommunityRoles } from '@/lib/nav-access';

import { CalendarContext } from './calendar-context';
import { EventContent } from './event-content';
import { EventDialog } from './event-dialog';
import type { ViewMode } from './types';
import type { ViewEventProps } from './view-event-dialog';
import { ViewEventDialog } from './view-event-dialog';
import { AccountRole } from '@/types/v2/account-role';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';

interface CreateDialogData {
  range?: { start: Date; end: Date };
}

const plugins = [dayGridPlugin, interactionPlugin, listPlugin, timeGridPlugin, timelinePlugin];

export interface CalendarViewProps {
  view?: ViewMode;
  calendarRef?: React.MutableRefObject<any>;
  onDateChange?: (date: Date) => void;
}

export function CalendarView({ view = 'dayGridMonth', calendarRef: externalRef, onDateChange }: CalendarViewProps): React.JSX.Element {
  const { events, currentEventId, setCurrentEventId, createEvent, deleteEvent, updateEvent, isLoading, isError } =
    React.useContext(CalendarContext);

  const { user, community, account } = useUser();
  const navigate = useNavigate();

  const internalRef = React.useRef<Calendar | null>(null);
  const calendarRef = externalRef ?? internalRef;

  const [date, setDate] = React.useState<Date>(new Date());

  const handleDateChange = React.useCallback((newDate: Date) => {
    setDate(newDate);
    onDateChange?.(newDate);
  }, [onDateChange]);

  const createDialog = useDialog<CreateDialogData>();

  const viewEventDialog = useDialog<ViewEventProps>();

  const currentEvent = currentEventId ? events.get(currentEventId) : undefined;

  // Check if user has edit permissions
  const hasEditPermissions = React.useMemo(() => {
    if (!user) return false;

    // Check account roles
    const accountRoles = account?.roles || [];
    const hasAccountRole = accountRoles.some((role: string) => 
      role === ACCOUNT_ROLES.ACCOUNT_OWNER || 
      role === ACCOUNT_ROLES.ACCOUNT_ADMIN ||
      role === ACCOUNT_ROLES.ACCOUNT_MANAGER
    );

    if (hasAccountRole) return true;

    // Check community roles. Board members count as community managers (as the
    // backend and RoleGuard both do) — without this a board member could create
    // an event from the header button, which is RoleGuard-gated and elevates
    // them, and then be unable to click it to edit or delete it.
    const communityRoles = effectiveCommunityRoles(community?.roles);
    const hasCommunityRole = communityRoles.some(role => 
      role === COMMUNITY_ROLES.COMMUNITY_MANAGER
    );

    return hasCommunityRole;
  }, [user, account, community]);

  const handleViewChange = React.useCallback(
    (value: ViewMode) => {
      const calendarApi = calendarRef.current?.getApi();

      if (calendarApi) {
        calendarApi.changeView(value);
      }

      navigate(`/calendar-events?view=${value}`);
    },
    [navigate]
  );

  const handleDateToday = React.useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
      handleDateChange(calendarApi.getDate());
    }
  }, [calendarRef, handleDateChange]);

  const handleDatePrev = React.useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
      handleDateChange(calendarApi.getDate());
    }
  }, [calendarRef, handleDateChange]);

  const handleDateNext = React.useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
      handleDateChange(calendarApi.getDate());
    }
  }, [calendarRef, handleDateChange]);

  const handleAdd = React.useCallback(() => {
    if (!hasEditPermissions) {
      // For non-admin users, show a message or redirect
      return;
    }
    createDialog.handleOpen();
  }, [createDialog, hasEditPermissions]);

  const handleRangeSelect = React.useCallback(
    (arg: DateSelectArg) => {
      if (!hasEditPermissions) {
        // For non-admin users, don't allow range selection
        return;
      }

      const calendarApi = calendarRef.current?.getApi();

      if (calendarApi) {
        calendarApi.unselect();
      }

      createDialog.handleOpen({ range: { start: arg.start, end: arg.end } });
    },
    [createDialog, hasEditPermissions]
  );

  const handleEventSelect = React.useCallback(
    (arg: EventClickArg) => {
      const eventId = String(arg.event.id);
      const event = events.get(eventId);

      // Past events stay editable (Google Calendar / Outlook behaviour) so an
      // organiser can correct or remove an event that has already started.
      if (hasEditPermissions) {
        // For admin users, open edit dialog
        setCurrentEventId(eventId);
      } else {
        // For non-admin users, open view-only dialog
        if (event) {
          viewEventDialog.handleOpen({ event });
        }
      }
    },
    [setCurrentEventId, events, hasEditPermissions, viewEventDialog]
  );

  const handleEventResize = React.useCallback(
    (arg: EventResizeDoneArg) => {
      if (!hasEditPermissions) {
        // For non-admin users, don't allow resizing
        return;
      }

      const { event } = arg;

      updateEvent(event.id, { allDay: event.allDay, start: event.start!, end: event.end! });
    },
    [updateEvent, hasEditPermissions]
  );

  const handleEventDrop = React.useCallback(
    (arg: EventDropArg) => {
      if (!hasEditPermissions) {
        // For non-admin users, don't allow dragging
        return;
      }

      const { event } = arg;

      updateEvent(event.id, { allDay: event.allDay, start: event.start!, end: event.end! });
    },
    [updateEvent, hasEditPermissions]
  );

  // Transform events for FullCalendar (use string ids for consistent eventClick lookup)
  const calendarEvents = React.useMemo(() => {
    return Array.from(events.values()).map((event) => ({
      id: String(event.id),
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      backgroundColor: 'transparent', // Let our custom component handle the styling
      borderColor: 'transparent',
      textColor: 'transparent', // Hide default text since we're using custom rendering
      extendedProps: {
        description: event.description,
        priority: event.priority,
        color: event.color,
        type: event.type,
      },
    }));
  }, [events]);

  return (
    <React.Fragment>
      <Box>
        {isLoading ? (
          <Stack spacing={1}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} height={100} sx={{ borderRadius: 1 }} variant="rectangular" />
            ))}
          </Stack>
        ) : isError ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CalendarBlankIcon color="var(--mui-palette-text-disabled)" size={48} style={{ marginBottom: 12 }} />
            <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body1">
              Could not load events
            </Typography>
            <Typography color="text.disabled" sx={{ mt: 0.5 }} variant="body2">
              Please try refreshing the page
            </Typography>
          </Box>
        ) : (
          <Calendar
            allDayMaintainDuration
            dayMaxEventRows={3}
            droppable={hasEditPermissions}
            editable={hasEditPermissions}
            eventClick={handleEventSelect}
            eventContent={EventContent}
            eventDisplay="block"
            eventDrop={handleEventDrop}
            eventMinHeight={25}
            eventResizableFromStart={hasEditPermissions}
            eventResize={handleEventResize}
            events={calendarEvents}
            headerToolbar={false}
            height={780}
            initialDate={date}
            initialView={view}
            plugins={plugins}
            ref={calendarRef}
            rerenderDelay={10}
            select={hasEditPermissions ? handleRangeSelect : undefined}
            selectable={hasEditPermissions}
            weekends
          />
        )}
      </Box>
      
      {/* Create Event Dialog - Only for users with edit permissions */}
      {createDialog.open && hasEditPermissions ? (
        <EventDialog
          action="create"
          onClose={createDialog.handleClose}
          onCreate={createEvent}
          open
          range={createDialog.data?.range}
        />
      ) : null}
      
      {/* Edit Event Dialog - Only for users with edit permissions and future events */}
      {currentEvent && hasEditPermissions ? (
        <EventDialog
          action="update"
          event={currentEvent}
          onClose={() => {
            setCurrentEventId(undefined);
          }}
          onCreate={createEvent}
          onDelete={(eventId) => {
            setCurrentEventId(undefined);
            deleteEvent(eventId);
          }}
          onUpdate={(eventId, params) => {
            updateEvent(eventId, params);
            setCurrentEventId(undefined);
          }}
          open
        />
      ) : null}
      
      {/* View Event Dialog - For non-admin users */}
      {viewEventDialog.open ? (
        <ViewEventDialog
          event={viewEventDialog.data?.event}
          onClose={viewEventDialog.handleClose}
          open={viewEventDialog.open}
        />
      ) : null}
    </React.Fragment>
  );
}
